import clsx from 'clsx';
import { type MouseEvent, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { selectPinnedCompanies } from '@/api/workspace/selectors.ts';
import { MaybePrimeBrokenBadge } from '@/App/tabs/MaybePrimeBrokenBadge.tsx';
import {
  filterClientsByTerm,
  partitionByPinState,
  sortClientsByCompanyName,
  sortClientsByPinIndex,
} from '@/App/tabs/TabMenu/tabMenu.utils.ts';
import { ClientMenuButton } from '@/components/ClientSelector/ClientMenuButton.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';

const MAX_CLIENTS_DISPLAYED = 50;

export type ClientsTableProps = { searchTerm: string; onSelectClient: (clientId: number) => void };

export const ClientsTable = ({ searchTerm, onSelectClient }: ClientsTableProps) => {
  const {
    currentUser: {
      clients,
      userPreferences: { accountNameLength },
    },
    defaultClient,
  } = useUserContext();

  const pinnedCompaniesIds = useAppSelector(selectPinnedCompanies);

  const filteredClients = useMemo(() => {
    const [pinnedClients, otherClients] = partitionByPinState(clients, pinnedCompaniesIds, defaultClient.companyId);
    return [
      [defaultClient],
      pinnedClients.sort(sortClientsByPinIndex(pinnedCompaniesIds)),
      otherClients.sort(sortClientsByCompanyName),
    ]
      .flat()
      .filter(filterClientsByTerm(searchTerm))
      .toSpliced(MAX_CLIENTS_DISPLAYED);
  }, [clients, searchTerm, pinnedCompaniesIds, defaultClient]);

  const isPinnedCompany = (companyId: number) =>
    companyId === defaultClient.companyId || pinnedCompaniesIds.includes(companyId);

  const onClickClient = (companyId: number) => (event: MouseEvent<HTMLTableRowElement>) => {
    event.stopPropagation();
    event.preventDefault();

    onSelectClient(companyId);
  };

  return (
    <table className="w-100 table table-sm table-hover">
      <thead>
        <tr>
          <th scope="col" style={{ width: '311px' }}>
            <FormattedMessage id="clientSelection.table.accountName" />
          </th>
          <th scope="col" style={{ width: '131px' }}>
            <FormattedMessage id="clientSelection.table.bdrId" />
          </th>
          <th scope="col">
            <FormattedMessage id="clientSelection.table.shortName" />
          </th>
          <th scope="col" />
        </tr>
      </thead>
      <tbody>
        {filteredClients.map((client) => (
          <tr
            className={clsx({ 'fw-bold': isPinnedCompany(client.companyId) })}
            key={client.companyId}
            style={{ cursor: 'pointer' }}
          >
            <td>
              <div
                className="d-flex justify-content-between align-items-center"
                onClick={onClickClient(client.companyId)}
                data-e2e="client-table-account-name"
              >
                <BoldSubstring
                  text={
                    accountNameLength === 'Short' ? client.companyName : (client.companyFullName ?? client.companyName)
                  }
                  substring={searchTerm}
                />
                <MaybePrimeBrokenBadge primeBroker={client.primeBroker} />
              </div>
            </td>
            <td>
              <div onClick={onClickClient(client.companyId)}>
                <BoldSubstring text={client.companyId.toString()} substring={searchTerm} />
              </div>
            </td>
            <td>
              <div onClick={onClickClient(client.companyId)}>
                <BoldSubstring text={client.externalCompanyName ?? ''} substring={searchTerm} />
              </div>
            </td>
            {client.companyId !== defaultClient.companyId ? (
              <ClientMenuButton
                companyId={client.companyId}
                // can't allow reorderig pinned companies whe filtering client
                // this would result in poor user experience
                preventReorderingPinned={searchTerm.length > 0}
              />
            ) : (
              <td />
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/**
 * Render a text with a given substring rendered bold.
 *
 * Used to hightlight a searched string in a text
 */
type BoldSubstringProps = { text: string; substring: string };

const BoldSubstring = ({ text, substring }: BoldSubstringProps) => {
  const substringPosition = text.toLocaleLowerCase().indexOf(substring.toLocaleLowerCase());

  if (substringPosition === -1) {
    return <span>{text}</span>;
  }

  const beforeSubstring = text.slice(0, substringPosition);
  const matchedSubstring = text.slice(substringPosition, substringPosition + substring.length);
  const afterSubstring = text.slice(substringPosition + substring.length);

  return (
    <span>
      {beforeSubstring}

      <span className="bg-buttercup">{matchedSubstring}</span>

      {afterSubstring}
    </span>
  );
};
